import React, { useEffect, useState } from 'react'
import * as RadixDialog from '@radix-ui/react-dialog'
import { tv } from 'tailwind-variants'
import Button from 'components/button'
import { MenuLayer } from 'components/menu'
import { Cross } from 'components/icons'

// In the manner of shadcn/ui's dialog, on Radix's: a dimmed page, and a card in the middle of the window
// with a title, a line saying what it's for, its content, and its buttons at the foot. Radix keeps focus in
// it while it's open and returns it after, stops the page behind from scrolling, closes it on Escape or a
// press outside it, and names it for screen readers by its title and description.
const dialog = tv({
  slots: {
    overlay: 'fixed inset-0 z-[3500] bg-black/40 animate-overlay-in motion-reduce:animate-none',
    // Centred by a flex box rather than a transform, so menus fixed inside it place themselves against the
    // window.
    position: 'fixed inset-0 z-[3500] flex items-center justify-center p-4 pointer-events-none',
    content: [
      'pointer-events-auto relative flex max-h-[min(720px,calc(100vh-32px))] w-full flex-col',
      'rounded-xl border border-divider bg-surface-raised text-[13px] text-body outline-none',
      'shadow-[0_16px_48px_-12px_rgb(0_0_0/0.25),0_4px_12px_-4px_rgb(0_0_0/0.08)]',
      'animate-dialog-in motion-reduce:animate-none'
    ],
    header: 'flex flex-col gap-1 px-5 pt-5',
    // Clear of the close button; the description below runs under it.
    title: 'pr-8 text-[16px] font-semibold leading-6 text-body',
    description: 'text-pretty text-muted',
    body: 'min-h-0 flex-1 overflow-y-auto px-5 py-4',
    // The quieter surface of the menus' and popovers' footers, to the card's rounded corners.
    footer: 'flex items-center justify-end gap-2 rounded-b-xl border-t border-divider bg-surface-muted px-5 py-3.5',
    close: 'absolute top-3.5 right-3.5 size-8 p-0',
    // A split dialog's body, between its full-width title and buttons: its settings, and beside them a panel
    // of its own (a preview of what the settings make), each scrolling on its own.
    // Its gutter widened into the dialog's padding, so rows that hang their padding into the gutter (a
    // switch's fill, a rule's pill) stay inside it rather than make it scroll sideways.
    main: '-mx-3 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-3',
    aside: 'flex w-[340px] shrink-0 flex-col overflow-hidden rounded-lg border border-divider bg-surface-faint',
    // A sidebar dialog's list of what it sets up, left of the settings: one picked at a time, a line between
    // it and the settings from the title's foot to the buttons'. Its rows' padding hangs into the gutter, so
    // their icons line up with the title.
    nav: '-my-4 -ml-2 flex w-[220px] shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-divider py-4 pr-3'
  },
  variants: {
    width: {
      small: { content: 'max-w-[420px]' },
      medium: { content: 'max-w-[460px]' },
      large: { content: 'max-w-[680px]' },
      // A file viewer: as wide as a page, with a compact header (a smaller title, its line close under it,
      // centred on the close button) so the file has the height.
      viewer: {
        content: 'max-w-[960px] max-h-[calc(100vh-32px)]',
        header: 'gap-0 pt-3',
        title: 'text-[14px] leading-5',
        close: 'top-3',
        body: 'pt-2.5 pb-5'
      },
      // As tall as it's allowed whatever's in it, so the pane beside has room.
      split: { content: 'h-[min(640px,calc(100vh-32px))] max-w-[880px]', body: 'flex gap-5 overflow-hidden' },
      // A split dialog with a sidebar too: room for the list, the settings and the panel beside them.
      wide: { content: 'h-[min(680px,calc(100vh-32px))] max-w-[1080px]', body: 'flex gap-5 overflow-hidden' }
    },
    // As tall as it's allowed whatever's in it: a list that's searched, or long, doesn't resize the dialog.
    tall: { true: { content: 'h-[min(640px,calc(100vh-32px))]' } }
  },
  defaultVariants: { width: 'medium' }
})

/** The foot of a split dialog's panel: the menus' grey band, as a panel's footer is. */
export const DIALOG_ASIDE_FOOTER =
  'flex h-10 shrink-0 items-center gap-2 border-t border-divider bg-surface-muted px-1.5'

const CLOSE_ALL_DIALOGS = 'morta:close-all-dialogs'

/**
 * Closes every open dialog but one that must be answered, for something that needs the page a dialog keeps to
 * itself: AI Support, opened from an error over one, couldn't otherwise be used.
 */
export const closeAllDialogs = (): void => {
  window.dispatchEvent(new Event(CLOSE_ALL_DIALOGS))
}

interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  /** A line under the title saying what the dialog is for. */
  description?: React.ReactNode
  /** Beside the title, before the close button: a ⋯ for what can be done to what the dialog shows. */
  titleAction?: React.ReactNode
  /** After the description, on its line: a small control for it, such as a file's full view. */
  descriptionEnd?: React.ReactNode
  /** At the header's end, before the close button: controls for the whole dialog, such as a viewer's arrows. */
  headerEnd?: React.ReactNode
  /** Its buttons, at the foot: Cancel, then the action, on the right. */
  footer?: React.ReactNode
  width?: 'small' | 'medium' | 'large' | 'viewer' | 'split' | 'wide'
  /** With width "split" or "wide": the panel to the right of the settings, inside the body. */
  aside?: React.ReactNode
  /** With width "wide": a sidebar left of the settings, listing what they're for. */
  nav?: React.ReactNode
  /** A fixed height, for a list that would otherwise grow and shrink the dialog as it's searched. */
  tall?: boolean
  /** False for a choice that must be made: no close button, and Escape or a press outside leaves it open. */
  dismissible?: boolean
  /**
   * Opened from a menu or popover that should stay open behind it, as Edit column does for a select's source:
   * a press in the dialog, or on the dimmed page around it, isn't a press outside that menu, and Escape
   * closes only the dialog.
   */
  nestedInMenu?: boolean
  children: React.ReactNode
}

/**
 * A modal dialog. Menus, dropdowns and popovers opened from its content render in a layer of its own, so
 * they count as inside it: focus can reach them, and pressing one doesn't close the dialog.
 */
const Dialog: React.FC<DialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  titleAction,
  descriptionEnd,
  headerEnd,
  footer,
  width,
  dismissible = true,
  nestedInMenu = false,
  aside,
  nav,
  tall,
  children
}) => {
  const [layer, setLayer] = useState<HTMLElement | null>(null)
  const styles = dialog({ width, tall })

  useEffect(() => {
    if (!open || !dismissible) return
    const close = () => onOpenChange(false)
    window.addEventListener(CLOSE_ALL_DIALOGS, close)
    return () => window.removeEventListener(CLOSE_ALL_DIALOGS, close)
  }, [open, dismissible, onOpenChange])

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={styles.overlay()} {...(nestedInMenu && { 'data-nested-menu': 'true' })} />
        <div className={styles.position()} {...(nestedInMenu && { 'data-nested-menu': 'true' })}>
          <RadixDialog.Content
            className={styles.content()}
            // Escape closes an open menu first, and the dialog only once none is.
            onEscapeKeyDown={(event) => {
              if (!dismissible || layer?.childElementCount) {
                event.preventDefault()
                return
              }
              // Radix hears Escape on the document as it goes down to the page, and this dialog is gone before the
              // key comes back up to the document, where the menu behind listens: with nothing open over it then, the
              // menu would close too (#11445). The Escape is this dialog's alone, so it goes no further. An Escape the
              // dialog doesn't close on, because a menu is open in it (above), goes on to that menu, which closes first;
              // the menu behind ignores it while the dialog is open over it.
              if (nestedInMenu) event.stopPropagation()
            }}
            onInteractOutside={(event) => {
              if (!dismissible) event.preventDefault()
            }}
            // Without a description, say so, rather than leaving screen readers a dangling reference.
            {...(description ? {} : { 'aria-describedby': undefined })}
          >
            <MenuLayer.Provider value={layer}>
              {/* With a sidebar, a line under the title too, so the sidebar's line runs from it to the buttons'. */}
              <div className={styles.header({ class: nav ? 'border-b border-divider pb-4' : undefined })}>
                {titleAction || headerEnd ? (
                  // The title's line: the title and its action, then the header's own controls, clear of the close
                  // button and centred on it.
                  <div className="flex min-h-8 min-w-0 items-center gap-1 pr-8">
                    <RadixDialog.Title className={styles.title({ class: 'min-w-0 pr-0' })}>{title}</RadixDialog.Title>
                    {titleAction}
                    {headerEnd && <div className="ml-auto flex shrink-0 items-center pl-3">{headerEnd}</div>}
                  </div>
                ) : (
                  <RadixDialog.Title className={styles.title()}>{title}</RadixDialog.Title>
                )}
                {description && (
                  <div className="flex min-w-0 items-center gap-1">
                    <RadixDialog.Description className={styles.description()}>{description}</RadixDialog.Description>
                    {descriptionEnd}
                  </div>
                )}
              </div>
              <div className={styles.body()}>
                {aside ? (
                  <>
                    {nav && <nav className={styles.nav()}>{nav}</nav>}
                    <div className={styles.main()}>{children}</div>
                    <aside className={styles.aside()}>{aside}</aside>
                  </>
                ) : (
                  children
                )}
              </div>
              {footer && <div className={styles.footer()}>{footer}</div>}
              {dismissible && (
                <RadixDialog.Close asChild>
                  <Button internalType="transparent" aria-label="Close" className={styles.close()}>
                    <Cross />
                  </Button>
                </RadixDialog.Close>
              )}
            </MenuLayer.Provider>
            {/* Where its menus render: the whole window, inside the dialog; only the menus take presses. */}
            <div ref={setLayer} className="pointer-events-none fixed inset-0 z-[1] [&>*]:pointer-events-auto" />
          </RadixDialog.Content>
        </div>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

export default Dialog
